import { formatMoney } from '@theme/money-formatting';

/**
 * ITT catalog sidebar: mobile drawer + lazy branch loading.
 * Accordion behaviour itself is native <details>. Any row with
 * [data-catalog-branch-url] fetches one level on first expand.
 */
class CollectionCatalogSidebar extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // `toggle` does not bubble — listen in the capture phase.
    this.addEventListener('toggle', (event) => this.#loadBranch(event), { signal, capture: true });
    this.addEventListener('input', (event) => this.#onPriceSliderInput(event), { signal });

    const openButton = this.querySelector('[data-catalog-sidebar-open]');
    const closeButton = this.querySelector('[data-catalog-sidebar-close]');
    const backdrop = this.querySelector('[data-catalog-sidebar-backdrop]');
    const sidebar = this.querySelector('.catalog-sidebar');

    if (!(openButton instanceof HTMLButtonElement) || !(sidebar instanceof HTMLElement)) return;

    openButton.addEventListener('click', () => this.#open(openButton, sidebar, backdrop), { signal });
    closeButton?.addEventListener('click', () => this.#close(openButton, sidebar, backdrop), { signal });
    backdrop?.addEventListener('click', () => this.#close(openButton, sidebar, backdrop), { signal });

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && sidebar.classList.contains('is-open')) {
          this.#close(openButton, sidebar, backdrop);
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * @param {Event} event
   */
  async #loadBranch(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    const content = details.querySelector(':scope > [data-catalog-branch-url]');
    if (!(content instanceof HTMLElement)) return;

    const url = content.dataset.catalogBranchUrl;
    if (!url) return;

    delete content.dataset.catalogBranchUrl;
    content.textContent = this.dataset.loadingLabel || '';
    content.classList.add('is-loading');

    try {
      const response = await fetch(url, {
        signal: this.#controller?.signal,
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) throw new Error(`Branch fetch failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const tree = doc.querySelector('[data-catalog-branch-tree]');

      content.classList.remove('is-loading');
      content.innerHTML = tree?.innerHTML.trim() || '';

      // A root without renderable children must not keep a chevron that opens nothing.
      if (!content.innerHTML) {
        details.open = false;
        details.classList.add('catalog-sidebar__branch--empty');
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      content.classList.remove('is-loading');
      content.textContent = '';
      content.dataset.catalogBranchUrl = url;
      details.open = false;
    }
  }

  /**
   * Dual-range slider writes values into the price text inputs (applied via Done).
   * @param {Event} event
   */
  #onPriceSliderInput(event) {
    const range = event.target;
    if (!(range instanceof HTMLInputElement) || range.dataset.priceRange == null) return;

    const slider = range.closest('.catalog-sidebar__price-slider');
    const facet = range.closest('price-facet-component');
    if (!(slider instanceof HTMLElement) || !(facet instanceof HTMLElement)) return;

    const minRange = slider.querySelector('[data-price-range="min"]');
    const maxRange = slider.querySelector('[data-price-range="max"]');
    const minInput = facet.querySelector('[ref="minInput"]');
    const maxInput = facet.querySelector('[ref="maxInput"]');
    if (
      !(minRange instanceof HTMLInputElement) ||
      !(maxRange instanceof HTMLInputElement) ||
      !(minInput instanceof HTMLInputElement) ||
      !(maxInput instanceof HTMLInputElement)
    ) {
      return;
    }

    const ceiling = Number(minRange.max) || 0;
    let minValue = Number(minRange.value) || 0;
    let maxValue = Number(maxRange.value) || 0;
    if (minValue > maxValue) {
      if (range.dataset.priceRange === 'min') {
        minValue = maxValue;
        minRange.value = String(minValue);
      } else {
        maxValue = minValue;
        maxRange.value = String(maxValue);
      }
    }

    // Mirror `money_without_currency`: shop format, minor units in, symbol stripped.
    const moneyFormat = atob(facet.dataset.moneyFormat || '') || '{{amount}}';
    const currency = facet.dataset.currency || 'USD';
    const toAmount = (cents) => formatMoney(cents, moneyFormat, currency).replace(/[^\d.,\s']/g, '').trim();
    minInput.value = minValue === 0 ? '' : toAmount(minValue);
    maxInput.value = maxValue === ceiling ? '' : toAmount(maxValue);

    if (ceiling > 0) {
      slider.style.setProperty('--price-progress-left', `${(minValue / ceiling) * 100}%`);
      slider.style.setProperty('--price-progress-right', `${100 - (maxValue / ceiling) * 100}%`);
    }
  }

  /**
   * @param {HTMLButtonElement} openButton
   * @param {HTMLElement} sidebar
   * @param {Element | null} backdrop
   */
  #open(openButton, sidebar, backdrop) {
    sidebar.classList.add('is-open');
    openButton.setAttribute('aria-expanded', 'true');
    backdrop?.removeAttribute('hidden');
    document.documentElement.classList.add('catalog-sidebar-open');
  }

  /**
   * @param {HTMLButtonElement} openButton
   * @param {HTMLElement} sidebar
   * @param {Element | null} backdrop
   */
  #close(openButton, sidebar, backdrop) {
    sidebar.classList.remove('is-open');
    openButton.setAttribute('aria-expanded', 'false');
    backdrop?.setAttribute('hidden', '');
    document.documentElement.classList.remove('catalog-sidebar-open');
  }
}

if (!customElements.get('collection-catalog-sidebar')) {
  customElements.define('collection-catalog-sidebar', CollectionCatalogSidebar);
}
